/** Public View-site copy for YogaKai (config/site). Bump SEED_REVISION when editing. */

export const YOGA_SITE_SEED_TYPE = 'yogakai';
export const YOGA_SITE_SEED_REVISION = 3;

export function yogaSiteDoc() {
  return {
    brand: 'YogaKai',
    accent: '#2d5d58',
    seedType: YOGA_SITE_SEED_TYPE,
    seedRevision: YOGA_SITE_SEED_REVISION,
    seededBy: 'yogakai',
    seededAt: new Date().toISOString(),
    hero: {
      kicker: 'Practice · Learn · Teach',
      title: 'YogaKai',
      sub: 'Timed pose-sequence classes with on-screen cues, a breathing guide, and a pose dictionary — plus a sequence builder for teachers.',
      cta: { label: 'Start practicing', href: '/login?next=/today' },
      art: 'c',
    },
    nav: [
      { label: 'Practice', href: '#practice' },
      { label: 'Plans', href: '#plans' },
      { label: 'Teach', href: '#teach' },
      { label: 'FAQ', href: '#faq' },
    ],
    sections: [
      {
        kind: 'features',
        id: 'practice',
        title: 'Your practice, one pose at a time',
        sub: 'Sign in for Today’s recommendation, the class library, and a full-screen timer player.',
        items: [
          {
            icon: '◎',
            title: 'Today',
            text: 'A greeting, your streak, a recommended class, plus continue last class and recent practice history.',
          },
          {
            icon: '☰',
            title: 'Library',
            text: 'Filter classes by style, audience, and skill — or browse 560+ Pocket Yoga stills in the Poses tab.',
          },
          {
            icon: '▸',
            title: 'Practice player',
            text: 'Full-screen timer: one pose at a time, countdown, on-screen cue, inhale/exhale guide, pause and skip.',
          },
          {
            icon: '✎',
            title: 'Teach',
            text: 'Teachers build ordered pose sequences with holds, cues, breath, tips, and mods — then preview in the player.',
          },
        ],
      },
      {
        kind: 'stats',
        items: [
          { value: '560+', label: 'pose stills' },
          { value: '3', label: 'membership plans' },
          { value: 'Timed', label: 'pose sequences' },
        ],
      },
      {
        kind: 'tiers',
        id: 'plans',
        title: 'Membership plans',
        sub: 'Preview prices — billing is not connected yet. Switch anytime from Membership.',
        items: [
          {
            name: 'Karma Yogi',
            price: 'Free',
            per: '',
            blurb: 'Beginner Hatha pack with the timer player.',
            bullets: [
              '3 beginner Hatha classes',
              'Timer practice player',
              'On-screen cues + breathing guide',
              'Pose dictionary',
            ],
            cta: { label: 'Start free', href: '/login?next=/today' },
          },
          {
            name: 'Guided Yogi',
            price: '$12',
            per: '/mo',
            blurb: 'Full library for daily practice.',
            bullets: [
              'Full class library',
              'Timer practice player',
              'On-screen cues + breathing guide',
              'Pose dictionary',
            ],
            cta: { label: 'Choose Guided', href: '/login?next=/membership' },
          },
          {
            name: 'Yoga Teacher',
            price: '$28',
            per: '/mo',
            blurb: 'Everything in Guided, plus the sequence builder.',
            bullets: [
              'Full class library',
              'Sequence builder (Teach)',
              'Tips & modifications in the player',
              'Pose dictionary + pose picker',
            ],
            cta: { label: 'Choose Teacher', href: '/login?next=/membership' },
          },
        ],
      },
      {
        kind: 'cards',
        id: 'paths',
        title: 'Three ways in',
        style: 'post',
        itemsFrom: 'items',
      },
      {
        kind: 'features',
        id: 'teach',
        title: 'Built for teachers too',
        items: [
          {
            icon: '1',
            title: 'Sequence builder',
            text: 'Order poses, set hold times, write cues and breath notes, and add tip/mod lines students see in the player.',
          },
          {
            icon: '2',
            title: 'Preview as practice',
            text: 'Open any draft in the same timer player members use — check pacing before you save.',
          },
          {
            icon: '3',
            title: 'Pose picker',
            text: 'Search the dictionary and drop stills into your sequence so every step has a clear shape.',
          },
        ],
      },
      {
        kind: 'quotes',
        id: 'voices',
        title: 'How it feels on the mat',
        items: [
          {
            quote: 'I open Today, start the recommended class, and the timer keeps me from watching the clock.',
            name: 'A. Rivera',
            role: 'Guided Yogi',
          },
          {
            quote: 'Building a short restorative sequence in Teach and previewing it in the player is the loop I wanted.',
            name: 'M. Okonkwo',
            role: 'Yoga Teacher',
          },
        ],
      },
      {
        kind: 'cta',
        id: 'join',
        title: 'Roll out your mat',
        sub: 'Pick a plan on first visit, set your level and preferred minutes, then start with Today.',
        button: { label: 'Sign in to YogaKai', href: '/login?next=/today' },
      },
      { kind: 'faq', id: 'faq', title: 'Questions, answered', itemsFrom: 'faq' },
    ],
    items: [
      {
        id: 'path-karma',
        kind: 'post',
        title: 'Karma path — beginner Hatha',
        tag: 'Karma Yogi',
        meta: '3 sample classes · timer player',
        hue: 150,
        excerpt: 'Start with the beginner Hatha pack: foundations, holds, and on-screen cues with a breathing guide.',
        body: 'Karma Yogi unlocks the beginner Hatha pack and the full practice player — countdown, cue text, and inhale/exhale guidance.\n\nAfter you sign in, choose Karma on the plan screen, optionally set your practice level and preferred minutes, then open Today for a recommended class.',
      },
      {
        id: 'path-guided',
        kind: 'post',
        title: 'Guided path — full library',
        tag: 'Guided Yogi',
        meta: 'Full catalog · filters · poses',
        hue: 168,
        excerpt: 'Browse the full class library by style, audience, and skill, plus the Pocket Yoga pose dictionary.',
        body: 'Guided Yogi opens the full sample library and the Poses dictionary (560+ stills).\n\nPractice still uses the timer player with practitioner cues. Spoken voice guidance is planned for a later build — this release is visual and on-screen.',
      },
      {
        id: 'path-teacher',
        kind: 'post',
        title: 'Teacher path — sequence builder',
        tag: 'Yoga Teacher',
        meta: 'Teach · tips & mods · preview',
        hue: 18,
        excerpt: 'Create and edit classes as ordered pose sequences, then preview them in the same player your students use.',
        body: 'Yoga Teacher includes the full library plus Teach: ordered poses with holds, cues, breath, tips, and modifications.\n\nSaves write the sequence used by the practice player. Tips and mods show in the player for the Teacher plan.',
      },
    ],
    faq: [
      {
        q: 'What is YogaKai?',
        a: 'A signed-in practice app for timed pose-sequence classes: Today recommendations, a class library, a full-screen practice player with cues and a breathing guide, a pose dictionary, and a Teach builder for teachers.',
      },
      {
        q: 'How do the plans differ?',
        a: 'Karma Yogi gets the beginner Hatha pack. Guided Yogi unlocks the full library. Yoga Teacher adds the sequence builder and tip/mod lines in the player. You can switch plans anytime under Membership (billing is preview-only for now).',
      },
      {
        q: 'Is there spoken audio?',
        a: 'Not in this build. The practice player shows on-screen cues and an inhale/exhale guide. Voice guidance may come later.',
      },
      {
        q: 'What happens on my first visit?',
        a: 'After sign-in you choose a plan, then optionally set practice level and preferred session length. Today then recommends a class that fits.',
      },
      {
        q: 'Can I create my own classes?',
        a: 'Yes — on the Yoga Teacher plan, open Teach to build or edit sequences and preview them in the practice player.',
      },
      {
        q: 'Where do the pose pictures come from?',
        a: 'Practice and the Poses tab use Pocket Yoga stills (560+). Each teach step can name a pose key so the player shows the matching picture.',
      },
    ],
    footer: { text: 'YogaKai — timed practice, clear cues, room to teach.' },
  };
}
